import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { AnimationUtils, FunctionUtils, KeyboardEventKey, LocalComponentStore, RxjsUtils } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngxs/store";
import { BehaviorSubject, combineLatestWith, debounceTime, map } from "rxjs";
import { MapSearchResult } from "../../models/map-search.models";
import { MapSearchActions } from "../../state/map-search.actions";
import { MapSearchState } from "../../state/map-search.state";

const MIN_SEARCH_QUERY_LENGTH = 1;
const SEARCH_DEBOUNCE_TIME_MS = 300;
const FADE_DURATION_MS = 100;
const AUTOFOCUS_DELAY_MS = 100;

interface MapSearchComponentState {
    placeholderText: string | undefined;
}

@UntilDestroy()
@Component({
    selector: "dtm-map-search",
    templateUrl: "./map-search.component.html",
    styleUrls: ["./map-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [AnimationUtils.fadeAnimation(FADE_DURATION_MS)],
    host: {
        "(click)": "focus($event)",
        "(document:click)": "blur()",
        "[class.focused]": "isFocused",
    },
    providers: [LocalComponentStore],
})
export class MapSearchComponent implements OnInit, AfterViewInit {
    @Input() public set placeholderText(value: string) {
        this.localStore.patchState({ placeholderText: value });
    }

    protected readonly store = inject(Store);

    @ViewChild("searchInput") private searchInput: ElementRef<HTMLInputElement> | undefined;
    @ViewChildren("resultItem") private resultItems!: QueryList<ElementRef<HTMLDivElement>>;

    @Output() public readonly closePanel = new EventEmitter<void>();

    protected readonly searchQueryFormControl = new FormControl<string | undefined>(this.store.selectSnapshot(MapSearchState.searchQuery));

    protected readonly searchResults$ = this.store.select(MapSearchState.searchResults).pipe(
        combineLatestWith(this.store.select(MapSearchState.searchQuery)),
        map(([results, searchQuery]) =>
            results?.map((result) => this.getHighlightedResult(result, searchQuery)).filter(FunctionUtils.isTruthy)
        )
    );
    protected readonly selectedResults$ = this.store.select(MapSearchState.selectedSearchResult);
    protected readonly isSearchProcessing$ = this.store.select(MapSearchState.isSearchProcessing);

    protected readonly placeholderText$ = this.localStore.selectByKey("placeholderText");

    protected readonly highlightedResultIndex$ = new BehaviorSubject<number>(-1);
    protected isFocused = false;

    constructor(private localStore: LocalComponentStore<MapSearchComponentState>) {
        localStore.setState({
            placeholderText: undefined,
        });
    }

    public ngOnInit() {
        this.searchQueryFormControl.valueChanges
            .pipe(debounceTime(SEARCH_DEBOUNCE_TIME_MS), RxjsUtils.filterFalsy(), untilDestroyed(this))
            .subscribe((value) => {
                if (value.length < MIN_SEARCH_QUERY_LENGTH) {
                    return;
                }

                this.store.dispatch(new MapSearchActions.SetSearchQuery(value));
            });

        this.store
            .select(MapSearchState.searchQuery)
            .pipe(untilDestroyed(this))
            .subscribe((value) => {
                if (this.searchQueryFormControl.value !== value?.trim()) {
                    this.searchQueryFormControl.setValue(value, { emitEvent: false });
                }
            });

        setTimeout(() => {
            this.searchInput?.nativeElement.focus();
        }, AUTOFOCUS_DELAY_MS);
    }

    public ngAfterViewInit() {
        this.resultItems.changes.pipe(untilDestroyed(this)).subscribe(() => {
            this.scrollToActiveResult();
        });
    }

    protected onInputKeyDown(event: KeyboardEvent) {
        const results = this.store.selectSnapshot(MapSearchState.searchResults);
        const selectedResult = this.store.selectSnapshot(MapSearchState.selectedSearchResult);

        if (!selectedResult && event.key === KeyboardEventKey.Escape) {
            this.closePanel.emit();

            return;
        }

        if (!results || results.length === 0) {
            return;
        }
        const maxIndex = results.length - 1;
        let index = this.highlightedResultIndex$.getValue();

        switch (event.key) {
            case KeyboardEventKey.ArrowDown:
                event.preventDefault();
                index = index >= maxIndex ? 0 : index + 1;

                this.highlightedResultIndex$.next(index);
                this.scrollToActiveResult();
                break;
            case KeyboardEventKey.ArrowUp:
                index = index <= 0 ? maxIndex : index - 1;
                this.highlightedResultIndex$.next(index);
                event.preventDefault();
                this.scrollToActiveResult();
                break;
            case KeyboardEventKey.Enter: {
                const result = results[index];
                if (result) {
                    this.selectResult(result);
                }
                break;
            }
            case KeyboardEventKey.Escape:
                this.blur();
                break;
        }
    }

    protected focus(event: Event) {
        event.stopPropagation();
        this.searchInput?.nativeElement?.focus();
        this.isFocused = true;
    }

    protected blur(): void {
        this.isFocused = false;
        this.searchInput?.nativeElement.blur();
    }

    private scrollToActiveResult() {
        const results = this.store.selectSnapshot(MapSearchState.searchResults);
        if (!this.resultItems || !results?.length) {
            return;
        }
        const item = this.resultItems.toArray()[this.highlightedResultIndex$.getValue()];
        item?.nativeElement?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }

    protected clearSearch() {
        this.store.dispatch(new MapSearchActions.SetSearchQuery(undefined));
        this.searchInput?.nativeElement.focus();
    }

    protected selectResult(result: MapSearchResult) {
        this.store.dispatch(new MapSearchActions.SelectSearchResult(result));
    }

    protected setHighlightedResultIndex(number: number) {
        this.highlightedResultIndex$.next(number);
    }

    private getHighlightedResult(result: MapSearchResult, searchQuery: string | undefined | null): MapSearchResult | undefined {
        if (!searchQuery) {
            return;
        }

        return {
            ...result,
            highlightedName: this.highlightText(result.name, searchQuery.trim()),
        };
    }

    private highlightText(text: string, searchQuery: string): string {
        if (!searchQuery) {
            return text;
        }

        const textLower = text.toLowerCase();
        const queryLower = searchQuery.toLowerCase();
        const queryIndex = textLower.indexOf(queryLower);

        if (queryIndex === -1) {
            return text;
        }

        const before = text.slice(0, queryIndex);
        const match = text.slice(queryIndex, queryIndex + searchQuery.length);
        const after = text.slice(queryIndex + searchQuery.length);

        return `${before}<em>${match}</em>${this.highlightText(after, searchQuery)}`;
    }
}
